import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import classnames from 'classnames';

import { Button } from 'ui-components/components/button';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { Text, TextType } from 'ui-components/components/text';
import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';

import type { LtlQuote } from '@shipstation/client-common/types/ltl';
import { quoteCardMessages } from './quote-card-messages';

import style from './quote-card.module.less';

type QuoteCardProps = {
  isSelected: boolean;
  onSelect: (quote: LtlQuote) => void;
  onViewDetails: (quote: LtlQuote) => void;
  quote: LtlQuote;
};

export const QuoteCard = ({ isSelected, onSelect, onViewDetails, quote }: QuoteCardProps) => {
  const intl = useIntl();

  const handleCardClick = useCallback(() => {
    onSelect(quote);
  }, [onSelect, quote]);

  const handleViewDetailsClick = useCallback(
    (e: React.MouseEvent | React.FormEvent) => {
      e.stopPropagation();
      onViewDetails(quote);
    },
    [onViewDetails, quote],
  );

  const formattedPrice = intl.formatNumber(quote.totalCharges, { style: 'currency', currency: 'USD' });
  const formattedTransitDays = intl.formatMessage(quoteCardMessages.transitDays, { days: quote.transitDays });

  return (
    <Button
      className={classnames(style['quote-card'], { [style['quote-card-selected']]: isSelected })}
      onClick={handleCardClick}
      theme={THEME_CONSTANTS.unstyled}
    >
      <FlexColumn gap={Gap.Space8}>
        <div className={style['quote-card-header']}>
          <FlexColumn className={style['carrier-info']} gap={Gap.Space4}>
            <div className={style['carrier-name-row']}>
              <FlexRow gap={Gap.Space8}>
                <Text className={style['carrier-name']} isBold textType={TextType.Paragraph} value={quote.carrierName} />
                {quote.isGuaranteed && (
                  <div className={style['quote-card-guaranteed']}>
                    <Text textType={TextType.Caption} value={quoteCardMessages.guaranteedBadge} />
                  </div>
                )}
              </FlexRow>
            </div>
            <Text className={style['service-name']} textType={TextType.Caption} value={quote.serviceName} />
          </FlexColumn>

          <FlexColumn className={style['price-info']} gap={Gap.Space4}>
            <Text className={style['total-charges']} isBold textType={TextType.Paragraph} value={formattedPrice} />
            <FlexColumn className={style['transit-info']}>
              <Text textType={TextType.Caption} value={formattedTransitDays} />
              <Text
                className={style['delivery-date']}
                textType={TextType.Caption}
                value={intl.formatMessage(quoteCardMessages.estimatedDeliveryLabel)}
              />
              <Text textType={TextType.Caption} value={quote.estimatedDeliveryDate} />
            </FlexColumn>
          </FlexColumn>
        </div>

        <div className={style['quote-card-footer']}>
          <Button onClick={handleViewDetailsClick} theme={THEME_CONSTANTS.link}>
            <Text textType={TextType.Caption} value={quoteCardMessages.viewDetailsButton} />
          </Button>
        </div>
      </FlexColumn>
    </Button>
  );
};
